import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { StockCategory } from './stock';

export interface Range {
  from: string;
  to: string;
  days: number;
}

export interface RoomsInsights {
  range: Range;
  roomCount: number;
  totals: { bookingsMade: number; cancelled: number; noShows: number; stays: number; nights: number; revenue: number; averageNights: number; averageRate: number; occupancyPct: number };
  byRoomType: { name: string; stays: number; nights: number; revenue: number }[];
  byRoom: { number: string; roomType: string; stays: number; nights: number; revenue: number }[];
  unassigned: number;
  madeByHour: number[];
  madeByWeekday: number[];
  checkInsByWeekday: number[];
  byMonth: { month: string; made: number; stays: number }[];
  madeByDay: { date: string; count: number }[];
  leadTime: { label: string; count: number }[];
  stayLength: { label: string; count: number }[];
  sources: { source: string; count: number }[];
  occupancy: { date: string; occupied: number; pct: number }[];
}

export interface RestaurantInsights {
  range: Range;
  totals: { paidBills: number; paidSales: number; averageBill: number; discounts: number; itemsSold: number; orders: number; ordered: number };
  topByQty: { name: string; department: string; qty: number; revenue: number }[];
  topByRevenue: { name: string; department: string; qty: number; revenue: number }[];
  byDepartment: { department: string; qty: number; revenue: number }[];
  byCategory: { category: string; qty: number; revenue: number }[];
  byStation: { station: string; revenue: number }[];
  byHour: { hour: number; revenue: number; qty: number; orders: number }[];
  byWeekday: number[];
  tenders: { tender: string; amount: number; count: number }[];
  waiters: { name: string; revenue: number; orders: number }[];
  tables: { name: string; orders: number }[];
  notSold: { name: string; category: string }[];
}

export type StockState = 'OUT' | 'LOW' | 'OK' | 'NEW';
export interface InventoryInsights {
  range: Range;
  category: StockCategory | null;
  totals: { ingredients: number; out: number; low: number; notCounted: number; receipts: number; wasteEvents: number };
  byCategory: { category: StockCategory; items: number; out: number; low: number; notCounted: number }[];
  regularlyBought: { id: string; name: string; unitWord: string; category: StockCategory; location: string | null; times: number; received: number; lastReceived: string | null; averageGapDays: number | null }[];
  stock: { id: string; name: string; unitWord: string; category: StockCategory; location: string | null; onHand: number; reorderLevel: number; state: StockState; usedPerDay: number; daysLeft: number | null }[];
  /** quantities are grouped by the word they are counted in (g, ml, pcs, sets...), never mixed */
  mostUsed: { unit: string; rows: { name: string; value: number }[] }[];
  mostWasted: { unit: string; rows: { name: string; value: number }[] }[];
  weekly: { week: string; receipts: number; waste: number }[];
}

export interface ActivityInsights {
  range: Range;
  total: number;
  byDay: { date: string; count: number }[];
  byHour: number[];
  byPerson: { id: string | null; name: string; role: string | null; events: number }[];
  byGroup: { group: string; count: number }[];
  topActions: { action: string; count: number }[];
}

/** One section's numbers for a period. While new numbers load, the previous ones stay on screen (the frame never jumps). */
export function useInsights<T>(section: 'rooms' | 'restaurant' | 'inventory' | 'activity', from: string, to: string, extra = '') {
  return useQuery({
    queryKey: ['insights', section, from, to, extra],
    placeholderData: keepPreviousData,
    queryFn: () => api<T>(`/insights/${section}?from=${from}&to=${to}${extra}`),
  });
}
